import { useRef, useState } from 'react';
import { FileText, Trash2, Upload } from 'lucide-react';
import { ALLOWED_MIME_TO_EXT, MAX_FILE_BYTES } from '@crm/shared';
import { Drawer, toast } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  useTransactionAttachments,
  useUploadAttachment,
  useDeleteAttachment,
} from '../../../hooks/useFinancialOps';
import { financialOpsService } from '../../../services/financial-ops.service';
import { openAuthenticatedBlob } from '../../../utils/blobs';
import { formatBytes, formatDateBR } from '../../../utils/format';
import { AuthenticatedImage } from './AuthenticatedImage';

const ACCEPT = [...new Set([...Object.values(ALLOWED_MIME_TO_EXT), 'jpeg'])]
  .map((ext) => `.${ext}`)
  .join(',');

const MAX_MB = MAX_FILE_BYTES / (1024 * 1024);

interface Props {
  transactionId: string | null;
  onClose: () => void;
}

export function AttachmentsDrawer({ transactionId, onClose }: Props) {
  const open = Boolean(transactionId);
  const { data, isLoading } = useTransactionAttachments(transactionId ?? '');
  const upload = useUploadAttachment();
  const del = useDeleteAttachment(transactionId ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const attachments = data?.data ?? [];

  async function handleFiles(files: FileList | null) {
    if (!files || !transactionId) {
      return;
    }
    await Promise.all(
      Array.from(files).map(async (file) => {
        try {
          await upload.mutateAsync({ transactionId, file });
          toast.success(`${file.name} enviado`);
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Erro ao enviar arquivo';
          toast.error(`${file.name}: ${msg}`);
        }
      })
    );
  }

  return (
    <Drawer open={open} onClose={onClose} title="Anexos">
      <div className="space-y-4">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-accent bg-accent/5' : 'border-border'
          }`}
        >
          <Upload size={20} className="mx-auto mb-2 text-ink-secondary" />
          <p className="text-sm text-ink-secondary">
            Arraste arquivos aqui ou clique para selecionar
          </p>
          <p className="text-xs text-ink-tertiary mt-1">PDF, JPG, PNG, WEBP — até {MAX_MB}MB</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="md" />
          </div>
        ) : attachments.length === 0 ? (
          <p className="text-sm text-ink-tertiary text-center py-4">Nenhum anexo ainda.</p>
        ) : (
          <ul className="space-y-2">
            {attachments.map((a: any) => {
              const isImage = a.mimeType.startsWith('image/');
              const downloadUrl = financialOpsService.attachmentDownloadUrl(a.id);
              return (
                <li
                  key={a.id}
                  className="flex items-center gap-3 border border-border rounded-md p-2"
                >
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await openAuthenticatedBlob(downloadUrl, a.mimeType);
                      } catch {
                        toast.error('Erro ao abrir anexo');
                      }
                    }}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    {isImage ? (
                      <AuthenticatedImage
                        url={downloadUrl}
                        alt={a.fileName}
                        className="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <FileText size={32} className="text-ink-secondary shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-ink truncate">{a.fileName}</p>
                      <p className="text-xs text-ink-tertiary">
                        {formatBytes(a.sizeBytes)} · {formatDateBR(a.uploadedAt)}
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={async () => {
                      if (!confirm(`Remover ${a.fileName}?`)) {
                        return;
                      }
                      try {
                        await del.mutateAsync(a.id);
                        toast.success('Anexo removido');
                      } catch (err: any) {
                        toast.error(err?.response?.data?.message || 'Erro ao remover');
                      }
                    }}
                    className="text-ink-secondary hover:text-danger p-1"
                    title="Remover"
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Drawer>
  );
}
